import { trans } from '@codebuild/cookie-jar/libs/translation/trans';

import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, ChartOptions, DoughnutController, Filler, Legend, LinearScale, LineElement, PointElement, RadialLinearScale, Title, Tooltip } from 'chart.js';
import { clone } from 'chart.js/helpers';
import React from 'react';
import { Bar, Bubble, Doughnut, Line, Pie, Radar, Scatter } from 'react-chartjs-2';
import { Subscribable } from '../../libs/subscribable';
import { UserMetaInterface } from '../share/interfaces/user.interface';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    BarElement,
    DoughnutController,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);

interface DatasetsProp {
    label: string;
    data: number[];
    borderColor: string | string[];
    backgroundColor: string | string[];
    yAxisID: string;
    borderWidth?: number;
}

export const options: ChartOptions<any> = {
    angleLines: {
        display: false
    },
    x: {},
    animation: false,
    maintainAspectRatio: false,
    legend: { display: true },
    responsive: true,
    interaction: {
        mode: 'index' as const,
        intersect: false,
    },
    layout: {
        padding: {
            left: 0,
            right: 0,
            top: 20,
            bottom: 0,
        },
    },
    stacked: false,
    plugins: {
        datalabels: {
            formatter: (val, context) => `${val}%`,
        },
        tooltip: {
            callbacks: {
                label: (ttItem) => `${ttItem.formattedValue}%`
            }
        },
        title: {
            display: false,
            text: '',
            rotation: 90,
            position: 'left'
        },
    },
    scales: {
        y: {
            type: 'linear' as const,
            display: true,
            position: 'left' as const,
            ticks: {
                stepSize: 2,
                callback: (value, index, values) => {
                    return `${value}%`;
                },
            },
        },
    },
};

const datasets = {
    label: 'Dataset 1',
    data: [1, 2, 3],
    borderColor: 'rgb(77,177,222)',
    backgroundColor: 'rgba(32,156,238,0.5)',
    yAxisID: 'y',
};

interface ChartStateProps {
    data: DatasetsProp;
}

export interface ColourInterface {
    borderColor: string;
    backgroundColor: string;
}

export const COLOURS: { genderColours: ColourInterface[], baseColours: ColourInterface[] } = {
    genderColours: [
        { borderColor: '#EF6BA4', backgroundColor: '#EF6BA4' },
        { borderColor: '#2995F8', backgroundColor: '#2995F8' },
        { borderColor: '#FE938C', backgroundColor: 'transparent' },
        { borderColor: '#6CD4FF', backgroundColor: 'transparent' },
        { borderColor: '#d5d07c', backgroundColor: '#d5d07c' },
    ],
    baseColours: [
        { borderColor: '#7259B5', backgroundColor: '#E3DEF0' },
        { borderColor: 'rgb(77,177,222)', backgroundColor: 'rgba(32,156,238,0.5)' },
        { borderColor: 'rgb(29,238,187)', backgroundColor: 'rgba(113,243,172,0.5)' },
        { borderColor: '#7259B5', backgroundColor: 'transparent' },
    ]
};

export interface GraphProps {
    data: number[];
    data2?: number[];
    labels?: string[];
    label?: string;
    title?: string;
    barThickness?: number;
    colorIndex?: number;
    colorIndex2?: number;
    user: UserMetaInterface;
    primaryOptionLabel?: string;
    secondaryOptionLabel?: string;
    chartName: 'Line' | 'Doughnut' | 'Pie' | 'Bar' | 'Radar' | 'Bubble' | 'Scatter';
    doNotDisplayLegend?: boolean;
    customBarColours?: { borderColor: string[], backgroundColor: string[] } | false;
    chartWrapper?: string;
    hideVertical?: boolean;
}

export class Chart extends Subscribable<GraphProps, ChartStateProps> {
    public colours = COLOURS.baseColours;

    public averageColors = COLOURS.genderColours;

    public state = { data: datasets };

    public componentDidMount() {
        this.setChartData();
    }

    public componentDidUpdate(prevProps: Readonly<GraphProps>, prevState: Readonly<ChartStateProps>, snapshot?: any) {
        if (prevProps.data !== this.props.data) {
            this.setChartData();
        }
    }

    public setChartData() {
        const colours = this.props?.customBarColours ? this.props.customBarColours : this.getColourByGender();

        this.setState({
            data: {
                ...this.state.data,
                ...colours,
                data: this.props.data,
                label: this.props?.primaryOptionLabel || '',
                borderWidth: 1,
            }
        });
    }

    public getColourByGender() {
        switch (this.props.user?.gender) {
            case 'account.edit.gender.female':
                return this.averageColors[0];
            case 'account.edit.gender.male':
                return this.averageColors[1];
            default:
                return this.averageColors[2];
        }
    }

    public getOptions() {
        const baseColour = this.props.colorIndex ? this.colours[this.props.colorIndex] : this.colours[0];
        const barThickness = this.props.barThickness ? { barThickness: this.props.barThickness } : {};
        const hideVerticalLines = this.props.hideVertical ? { x: { grid: { display: false } } } : {};

        const blockOptions = {
            ...options,
            ...baseColour,
            ...barThickness,
            scales: {
                ...options.scales,
                ...hideVerticalLines
            }
        };

        if (this.props.doNotDisplayLegend) {
            blockOptions.plugins.legend = { display: false };
            blockOptions.plugins = { ...blockOptions.plugins, legend: { display: false } };
        }

        if (this.props.title) {
            blockOptions.plugins = {
                ...blockOptions.plugins, title: {
                    display: true,
                    align: 'center',
                    text: this.props.title
                }
            };
        }

        return blockOptions;
    }

    public render() {
        const secondaryColour = !isNaN(+(this.props?.colorIndex2 as any)) ? this.averageColors[this.props.colorIndex2 as any] : this.getColourByGender();
        const secondOption = { ...clone(this.state.data), ...secondaryColour };
        secondOption.data = this.props.data2 || [];
        secondOption.label = this.props?.secondaryOptionLabel || '';
        const labels = (this.props?.labels || []).map(label => trans(label));
        const chartDatasets = this.props.data2 ? [this.state.data, secondOption] : [this.state.data];
        const chartWrapper = this.props.chartWrapper ? this.props.chartWrapper : 'min-height-300';
        const blockOptions = this.getOptions();

        switch (this.props.chartName) {
            case 'Line':
                return <Line data={{ datasets: chartDatasets, labels: this.props.labels }} options={blockOptions}/>;
            case 'Doughnut':
                return <Doughnut data={{ datasets: chartDatasets, labels: this.props.labels }} options={blockOptions}/>;
            case 'Pie':
                return <Pie data={{ datasets: chartDatasets, labels: this.props.labels }} options={blockOptions}/>;
            case 'Bubble':
                return <Bubble data={{ datasets: chartDatasets, labels: this.props.labels }}/>;
            case 'Scatter':
                return <Scatter data={{ datasets: chartDatasets, labels: this.props.labels }}/>;
            case 'Bar':
                return <div className={chartWrapper}><Bar data={{ labels, datasets: chartDatasets }} options={blockOptions}/></div>;
            case 'Radar':
                return <div className={chartWrapper}><Radar data={{ labels, datasets: chartDatasets }} options={{ plugins: { legend: { position: 'bottom' } } }}/></div>;
            default:
                return <Pie data={{ datasets: chartDatasets, labels: this.props.labels }} options={blockOptions}/>;
        }
    }
}
